/**
 * Represents a standardized Product entity.
 * Formats the raw API product data into a clean structure for the UI.
 */
export const createProductModel = (data) => {
  if (!data) return null;

  // ✅ PRIMARY: Parse flags from hidden markers embedded in description
  // Supports new format [[ELLA_DATA:{}]] and old format <!--ELLA_META:{}-->
  let metaFlags = {};

  // 1. Ghost Variant Extraction (Bulletproof Tunnel)
  if (Array.isArray(data.variants)) {
    const ghostVariant = data.variants.find(v => v.color === 'ELLA_GHOST_DATA');
    if (ghostVariant && ghostVariant.size) {
      try {
        const cleanJson = ghostVariant.size
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .trim();
        // Only attempt JSON.parse if it looks like a JSON object
        if (cleanJson.startsWith('{')) {
          metaFlags = JSON.parse(cleanJson);
        }
      } catch (e) {
        console.warn('Ghost Variant Meta Parsing failed', e);
      }
    }
  }

  // 2. Fallback to Description markers if Ghost Variant missing
  if (Object.keys(metaFlags).length === 0 && data.description && typeof data.description === 'string') {
    // Try new format first (Protected from HTML strippers)
    const newMatch = data.description.trim().match(/\[\[ELLA_DATA:([\s\S]*?)\]\]/);
    if (newMatch && newMatch[1]) {
      try {
        // Handle potential HTML encoding of JSON (e.g. &quot;)
        const cleanJson = newMatch[1]
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .trim();
        metaFlags = JSON.parse(cleanJson);
      } catch (e) {
        console.warn('New Meta Parsing failed', e);
      }
    }
    // Fallback to old format
    if (Object.keys(metaFlags).length === 0) {
      const oldMatch = data.description.match(/<!--ELLA_META:([\s\S]*?)-->/);
      if (oldMatch && oldMatch[1]) {
        try {
          const cleanJson = oldMatch[1]
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .trim();
          metaFlags = JSON.parse(cleanJson);
        } catch (e) {
          console.warn('Old Meta Parsing failed', e);
        }
      }
    }
  }

  return {
    _id: data._id || null,
    id: data.id || data._id || null,
    title: data.title || '',
    description: (() => {
      // Prioritize tunneled real description if backend stripped it completely
      if (metaFlags.realDescription) return metaFlags.realDescription;

      const rawDesc = data.description || '';
      const desc = typeof rawDesc === 'string' ? rawDesc : String(rawDesc);
      
      // Clean up both marker formats from the display description
      return desc
        .replace(/\[\[ELLA_DATA:[\s\S]*?\]\]/g, '')
        .replace(/<!--ELLA_META:[\s\S]*?-->/g, '')
        .trim();
    })(),
    categoryId: data.categoryId || null,
    categoryName: (data.categoryId && data.categoryId.name)
      ? data.categoryId.name
      : (typeof data.categoryId === 'string' ? data.categoryId : 'Premium'),
    image: (() => {
      if (Array.isArray(metaFlags.images) && metaFlags.images.length > 0) return metaFlags.images[0];
      if (typeof metaFlags.image === 'string' && metaFlags.image) return metaFlags.image;
      if (typeof data.image === 'string' && data.image) return data.image;
      if (Array.isArray(data.images) && data.images.length > 0) return data.images[0];
      if (typeof data.imageUrl === 'string' && data.imageUrl) return data.imageUrl;
      if (typeof data.thumbnail === 'string' && data.thumbnail) return data.thumbnail;
      return '';
    })(),
    images: (() => {
      if (Array.isArray(metaFlags.images) && metaFlags.images.length > 0) return metaFlags.images;
      if (typeof metaFlags.image === 'string' && metaFlags.image) return [metaFlags.image];
      if (Array.isArray(data.images) && data.images.length > 0) return data.images;
      const single = data.image || data.imageUrl || data.thumbnail;
      if (typeof single === 'string' && single) return [single];
      if (Array.isArray(data.imageUrls) && data.imageUrls.length > 0) return data.imageUrls;
      return [];
    })(),
    variants: (() => {
      if (!data.variants || !Array.isArray(data.variants)) return [];
      
      // Remove the invisible Ghost Tunnel Variant so the UI never renders it
      const visibleVariants = data.variants.filter(v => v.color !== 'ELLA_GHOST_DATA');

      // Hydrate colorImages magically from tunneled metaFlags if backend dropped them
      if (metaFlags.colorImageMap && typeof metaFlags.colorImageMap === 'object') {
          return visibleVariants.map(v => ({
              ...v,
              colorImage: v.colorImage || metaFlags.colorImageMap[v.color] || ''
          }));
      }
      return visibleVariants;
    })(),
    tags: data.tags || [],
    productType: data.productType || 'normal',

    // ✅ CLEAN UNIFIED FLAGS (With Extreme Backwards Compatibility)
    isTrending: !!(metaFlags.isTrending || data.isTrending || data.trending || data.productType === 'trending'),
    isNewArrival: !!(
      metaFlags.isNewArrival || 
      data.isNewArrival || 
      data.newArrival || 
      data.isNewArrivals || 
      data.productType === 'newArrival'
    ),
    isBestSelling: !!(
      metaFlags.isBestSelling || 
      data.isBestSelling || 
      data.isBestSeller || 
      data.bestSelling || 
      data.productType === 'bestSelling'
    ),
    isTopRated: !!(
      metaFlags.isTopRated || 
      data.isTopRated || 
      data.topRated || 
      data.productType === 'topRated'
    ),
    isClearance: !!(metaFlags.isClearance || data.isClearance || data.productType === 'clearance'),

    isActive: data.isActive !== undefined ? data.isActive : true,
    totalStock: data.totalStock !== undefined ? data.totalStock : (data.stock || 0),
    totalSold: data.totalSold || 0,
    averageRating: data.averageRating || 0,
    slug: data.slug || '',

    price: (() => {
      const v = data.variants?.[0] || {};
      return v.finalPrice || v.price || 0;
    })(),

    createdAt: data.createdAt ? new Date(data.createdAt) : null,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
  };
};

